import * as React from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";

import { Button } from "@/components/ui/button";

import { useConsent } from "./ConsentProvider";

/**
 * SPEC-COOKIE-2 — bottom bar shown until the visitor records a current
 * decision (cookie missing OR stored policyVersion is stale). Hides
 * itself once `needsDecision` flips to false. Renders at z-50 so it
 * overlays every page (incl. /login + the cookie policy page itself).
 *
 * SPEC-COOKIE-4 — when `needsDecision` is true, also paint a fixed
 * full-viewport scrim BEHIND the bar (z-40, banner stays z-50) and lock
 * body scroll so the page is dim + unclickable until the visitor decides.
 *
 * KVKK invariant: both Accept and Reject are EQUAL exits. The overlay
 * has no dismiss affordance of its own (no close X, scrim click does
 * nothing, ESC does nothing) — the ONLY ways out are the banner's three
 * actions, and reject is just as effective as accept (this site has no
 * non-essential cookies today, so reject fully works).
 */
export function CookieConsentBanner() {
  const { t } = useTranslation();
  const { needsDecision, acceptAll, rejectAll, openManager } = useConsent();

  // SPEC-COOKIE-5 Part A — the cookie policy page must be readable
  // BEFORE the visitor decides, so the COOKIE-4 blocking overlay + body
  // scroll-lock are suppressed there. The banner bar still renders so
  // the visitor can decide from the policy page itself; only the scrim
  // and the scroll-lock are gated.
  const location = useLocation();
  const onPolicyPage = location.pathname === "/cerez-politikasi";
  const showOverlay = needsDecision && !onPolicyPage;

  // SPEC-COOKIE-4 — lock body scroll while the overlay is up. Keying off
  // `showOverlay` (not just `needsDecision`) ensures the policy page
  // stays scrollable even while a decision is pending.
  React.useEffect(() => {
    if (!showOverlay) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [showOverlay]);

  if (!needsDecision) return null;

  return (
    <>
      {/* SPEC-COOKIE-4 — scrim. Plain div (no shadcn) so the visual is
          identical to the portal's overlay. z-40 sits below the banner
          (z-50) and below the Manage modal (Radix Dialog overlay). No
          onClick / no role: clicking the scrim must NOT dismiss without
          a decision.
          SPEC-COOKIE-5 — suppressed on `/cerez-politikasi` so the policy
          page is readable while the visitor decides. */}
      {showOverlay && (
        <div
          data-testid="cookie-overlay"
          aria-hidden="true"
          className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm"
        />
      )}
      {/* SPEC-COOKIE-4b — bar surface is solid + lifted by an upward
          shadow so it reads as a raised sheet above the SPEC-COOKIE-4
          scrim. The `border-t` is preserved; the previous
          backdrop-blur/supports-* fallback is dropped because the bar is
          now opaque. */}
      <div
        role="dialog"
        aria-label={t("consent.banner.aria", { defaultValue: "Çerez tercihleri" })}
        data-testid="cookie-banner"
        className="fixed inset-x-0 bottom-0 z-50 border-t border-border bg-background shadow-[0_-8px_30px_rgba(0,0,0,0.5)]"
      >
      <div className="mx-auto flex max-w-6xl flex-col gap-4 px-4 py-6 text-base sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          {/* SPEC-COOKIE-4b — optional bold lead line for clarity. */}
          <p className="text-base font-semibold">
            {t("consent.banner.title", { defaultValue: "Çerezler" })}
          </p>
          <p className="mt-1 text-foreground/90">
            {t("consent.banner.text")}{" "}
            <Link
              to="/cerez-politikasi"
              className="underline underline-offset-2 hover:text-foreground"
              data-testid="cookie-banner-policy-link"
            >
              {t("consent.policyLink")}
            </Link>
            .
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:shrink-0">
          {/* SPEC-COOKIE-4b — bumped from size="sm" to the default size.
              Manage + Reject are equal-weight outline peers; Accept stays
              the default solid variant. KVKK: Reddet is the SAME size /
              same prominence as Kabul Et — only the fill differs. */}
          <Button
            variant="outline"
            onClick={openManager}
            data-testid="cookie-manage"
          >
            {t("consent.banner.manage")}
          </Button>
          <Button
            variant="outline"
            onClick={rejectAll}
            data-testid="cookie-reject"
          >
            {t("consent.banner.reject")}
          </Button>
          <Button
            onClick={acceptAll}
            data-testid="cookie-accept-all"
          >
            {t("consent.banner.acceptAll")}
          </Button>
        </div>
      </div>
      </div>
    </>
  );
}
